// Global Instructions Rule Applied!

import React from 'react'
import { Tag, Tooltip } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEnvelope, faRobot } from '@fortawesome/free-solid-svg-icons'
import dayjs from 'dayjs'
import type { AgentProvenanceEntry } from '../../state_manager/data_models/agent_provenance_entry.ts'

const DATE_FORMAT = 'MM/DD/YYYY'

interface AgentProvenanceTagProps {
  /**
   * The record's `prog__created_provenance`. Null/undefined for every record a
   * human created, which is the overwhelming majority — the component renders
   * nothing at all in that case, so callers can drop it in unconditionally.
   */
  provenance?: AgentProvenanceEntry | null
  /**
   * Cap on the tag's width before the sentence ellipsises; the Tooltip always
   * has it in full. The default clears a typical sentence ("Created by Claude
   * (dev agent) on 08/18/2026 for Rauten Hofmeyr" measures ~374px), so only
   * unusually long agent or user names truncate.
   */
  maxWidth?: number
  /**
   * Dense rendering for list/feed rows: the blue Tag and the robot icon only,
   * no sentence. The full sentence and the raw actor key stay in the Tooltip,
   * and the icon carries the sentence as an aria-label so the marker is not
   * icon-only to a screen reader. `maxWidth` is ignored in this mode -- there
   * is no text to truncate. Default false: the full sentence, as the four full
   * forms render it.
   */
  compact?: boolean
}

/**
 * AgentProvenanceTag — "Created by Claude (dev agent) on 08/18/2026 for Rauten
 * Hofmeyr".
 *
 * Agent Access 4.5 stamps `created_actor='agent:<key>'` on every row an agent
 * session creates, from the signed session claim; human writes leave it NULL.
 * This is the app-side face of that marker: it tells a reader, at a glance on
 * the record itself, that a record they are looking at was written by an agent
 * rather than typed by a person — and on whose behalf.
 *
 * The inbound command mailbox writes the same column with
 * `created_actor='mailbox:<sender>'` (mode 'mailbox'): the sender forwarded an
 * instruction email and the system created the record as them. Same tag,
 * envelope instead of robot, "Created via email by <sender> on <date>".
 *
 * One component for all four full forms (deal, company, contact, prospect)
 * rather than four copies. A new entity surfaces the marker by projecting
 * `prog__created_provenance` from its detail RPC and dropping this in beside
 * the form's existing header controls.
 *
 * Blue per the house Tag vocabulary — this is reference information, not a
 * status or a warning. Dates render `MM/DD/YYYY`, also per house rule.
 */
export function AgentProvenanceTag({
  provenance,
  maxWidth = 420,
  compact = false
}: AgentProvenanceTagProps): React.ReactElement | null {
  if (!provenance) return null

  // agent_name already COALESCEs to the bare key in SQL for a retired product;
  // this last fallback only matters if the object arrives without either.
  const agentName = provenance.agent_name || provenance.agent_key || 'an agent'
  const createdOn = provenance.date_created ? dayjs(provenance.date_created) : null
  const onDate = createdOn?.isValid() ? createdOn.format(DATE_FORMAT) : null
  const forUser = provenance.created_by_name

  // Two provenance stories, deliberately worded differently.
  //
  // 'live'     the agent both drafted AND committed the record -- the write
  //            funnel stamped created_actor from the signed session claim.
  // 'approved' the agent only DRAFTED it; a human reviewed the draft and the
  //            backend replayed the write under that human's own session. So
  //            created_by is the approver, not merely the delegating user, and
  //            saying "Created by <agent>" would misstate who committed it.
  //
  // 'mailbox'  no agent at all: the SENDER forwarded a command email and the
  //            system created the record as them. agent_key is their address;
  //            created_by_name is them.
  //
  // Anything else -- including a mode this build has never heard of, or the
  // field being absent on an older payload -- falls through to the live
  // wording, which is the safe default.
  const isMailbox = provenance.mode === 'mailbox'
  const sender = forUser || provenance.agent_key
  const sentence = isMailbox
    ? ['Created via email', sender ? ` by ${sender}` : '', onDate ? ` on ${onDate}` : ''].join('')
    : provenance.mode === 'approved'
      ? [
          `Drafted by ${agentName}`,
          forUser ? `, approved by ${forUser}` : '',
          onDate ? ` on ${onDate}` : ''
        ].join('')
      : [
          `Created by ${agentName}`,
          onDate ? ` on ${onDate}` : '',
          forUser ? ` for ${forUser}` : ''
        ].join('')

  return (
    <Tooltip
      title={
        <span>
          {sentence}
          {provenance.created_actor || provenance.agent_key ? (
            <>
              <br />
              <span className='text-xs opacity-75'>
                {provenance.created_actor ??
                  (isMailbox ? `mailbox:${provenance.agent_key}` : `agent:${provenance.agent_key}`)}
              </span>
            </>
          ) : null}
        </span>
      }
    >
      <Tag
        color='blue'
        data-debug='agent-provenance-tag'
        data-agent-key={provenance.agent_key ?? undefined}
        data-provenance-mode={provenance.mode ?? undefined}
        aria-label={compact ? sentence : undefined}
        className={
          compact
            ? 'm-0 text-xs inline-flex items-center px-1.5 leading-none'
            : 'm-0 text-xs inline-flex items-center gap-1 max-w-full overflow-hidden'
        }
        style={compact ? undefined : { maxWidth }}
      >
        <FontAwesomeIcon icon={isMailbox ? faEnvelope : faRobot} />
        {compact ? null : <span className='truncate'>{sentence}</span>}
      </Tag>
    </Tooltip>
  )
}

export default AgentProvenanceTag
